import {Task} from '../models/types';
import {AppSignals} from './AppSignals';
import {Signal} from "./Signals";

export interface TaskEditState {
	taskId: string | null;
	isEditing: boolean;
	originalTask: Task | null;
	hasChanges: boolean;
	isSaving: boolean;
}

export interface ITaskEditController {
	// Состояние
	readonly state: TaskEditState;

	// Сигналы
	readonly onEditStart: Signal<string>; // taskId
	readonly onEditEnd: Signal<void>;
	readonly onTaskUpdate: Signal<Task>;
	readonly onTaskDelete: Signal<string>; // taskId (soft delete)

	// Методы
	startEdit(taskId: string): Promise<void>;

	updateTask(updates: Partial<Task>): Promise<void>;

	cancelEdit(): void;

	deleteTask(): Promise<void>; // Мягкое удаление (устанавливает deletedAt)
	getCurrentTask(): Task | null;
}

export class TaskEditController implements ITaskEditController {
	private _state: TaskEditState = {
		taskId: null,
		isEditing: false,
		originalTask: null,
		hasChanges: false,
		isSaving: false
	};

	public readonly onEditStart = new Signal<string>("/edit/start");
	public readonly onEditEnd = new Signal<void>("/edit/end");
	public readonly onTaskUpdate = new Signal<Task>("/task/update");
	public readonly onTaskDelete = new Signal<string>("/task/delete");

	constructor(
		private getTaskById: (id: string) => Promise<Task | null>,
		private updateTaskInStorage: (id: string, updates: Partial<Task>) => Promise<Task>,
		private deleteTaskInStorage: (id: string) => Promise<void>
	) {
	}

	get state(): TaskEditState {
		return {...this._state};
	}

	async startEdit(taskId: string): Promise<void> {
		if (this._state.isEditing) {
			await this.cancelEdit();
		}

		const task = await this.getTaskById(taskId);
		if (!task) {
			throw new Error(`Task ${taskId} not found`);
		}

		this._state = {
			taskId,
			isEditing: true,
			originalTask: JSON.parse(JSON.stringify(task)), // Deep clone
			hasChanges: false,
			isSaving: false
		};

		this.onEditStart.emit(taskId);
	}

	async startInlineEdit(taskId: string, mode: 'title'): Promise<void> {
		return this.startEdit(taskId); // Пока один режим
	}

	public isChanged(updates:Partial<Task>): boolean{
		if (!this._state.taskId || !this._state.originalTask) return true;

		for(const key in updates){
			if (Object.prototype.hasOwnProperty.call(updates, key)) {
				if ( updates[key as keyof Task] !== this._state.originalTask[key as keyof Task]){
					return true;
				}
			}
		}
		return false;
	}

	async updateTask(updates: Partial<Task>): Promise<void> {
		if (!this._state.taskId || !this._state.originalTask) {
			throw new Error('No task is being edited');
		}

		this._state.isSaving = true;
		this._state.hasChanges = true;
		const updatedTask = {
			...this._state.originalTask,
			...updates,
			updatedAt: new Date(),
			isSynced: false
		};

		// Сохраняем в хранилище
		const savedTask = await this.updateTaskInStorage(
			this._state.taskId,
			{...updates, updatedAt: new Date(), isSynced: false}
		);

		// Обновляем состояние
		this._state.originalTask = savedTask;
		this._state.isSaving = false;

		this.onTaskUpdate.emit(savedTask);
	}


	cancelEdit(): void {
		if (!this._state.isEditing) return;

		this._state = {
			taskId: null,
			isEditing: false,
			originalTask: null,
			hasChanges: false,
			isSaving: false
		};

		this.onEditEnd.emit();
	}

	async deleteTask(): Promise<void> {
		if (!this._state.taskId) {
			throw new Error('No task is being edited');
		}

		this._state.isSaving = true;

		try {
			// Мягкое удаление - устанавливаем deletedAt
			await this.updateTaskInStorage(this._state.taskId, {
				deletedAt: new Date(),
				updatedAt: new Date(),
				isSynced: false
			});

			this.onTaskDelete.emit(this._state.taskId);
			AppSignals.appInvalidated.emit();

			// Выходим из режима редактирования
			this.cancelEdit();
		} catch (error) {
			this._state.isSaving = false;
			throw error;
		}
	}

	getCurrentTask(): Task | null {
		return this._state.originalTask;
	}

	getEditMode(): 'inline' | 'full' {
		return 'inline'; // Пока только inline режим
	}
}